import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@app/components/ui/card'
import { formatPairs, type DashboardProductionSummary } from './api'

/**
 * Work in production — by process.
 *
 * Shows the process codes (01…08) in route order; that order IS the physical
 * line on the factory floor (KESİM → SAYA → MONTE → ÜSTE → TEMİZLEME → SARAÇ),
 * so the rows sit like stops on a rail. Each bar has two parts:
 *
 *   • left, accent color: pairs on the bench (`Urtx_con_gch`, EndTarih null)
 *   • right, ink tone: pairs waiting in the queue (`Urtx_wait_gch`)
 *
 * Keeping the accent on the left and aligned turns "work actually being touched
 * right now" into a single scannable column; the bottleneck (longest grey tail)
 * sits right next to it. No chart library — two divs and percentage widths.
 */

export function WipRoute({ production }: { production: DashboardProductionSummary }) {
  const highest = production.processes.reduce((acc, p) => Math.max(acc, p.totalPairs), 0)

  return (
    <Card className="flex min-w-0 flex-col">
      <CardHeader>
        <div>
          <CardTitle>Üretimdeki iş</CardTitle>
          <CardDescription>Prosese göre atölyedeki çift — hat sırasıyla.</CardDescription>
        </div>
        <Legend />
      </CardHeader>

      <CardContent className="flex-1">
        {production.processes.length === 0 ? (
          <p className="py-6 text-center text-data text-ink-muted">
            Şu anda hiçbir proseste iş yok. Üretim emri açıldığında bu liste dolar.
          </p>
        ) : (
          // The thin rail on the left ties the processes into route order.
          <ul className="relative flex flex-col gap-1.5 before:absolute before:bottom-3 before:left-[1.0625rem] before:top-3 before:w-px before:bg-hairline">
            {production.processes.map((process) => {
              const inProgressPct = highest > 0 ? (process.inProgressPairs / highest) * 100 : 0
              const waitingPct = highest > 0 ? (process.waitingPairs / highest) * 100 : 0
              const reading = `${process.name}: ${formatPairs(process.inProgressPairs)} çift tezgâhta, ${formatPairs(process.waitingPairs)} çift sırada`

              return (
                /*
                 * The whole row opens the production screen — this chart's job
                 * is to point at the bottleneck, and the next question is
                 * always "which emirs are stuck there". The link spans the row
                 * (`after:inset-0`) so the bar and the number are click targets
                 * too, and it carries the row's reading as its tooltip because
                 * the overlay sits above the bar's own `title`.
                 *
                 * NOTE FOR THE MERGE: `?proses=` is inert until the üretim
                 * screen reads it (it has no URL state today) — the process
                 * code is `Urtx_*.Proses` ('01'…'08'), the same vocabulary the
                 * board and the API use.
                 */
                <li
                  key={process.code}
                  className="relative grid grid-cols-[2.125rem_minmax(5.5rem,auto)_1fr_auto] items-center gap-x-2.5 rounded-xs transition-colors hover:bg-kagit/70 sm:gap-x-3"
                >
                  <span
                    aria-hidden
                    className="relative z-1 grid h-5 w-[2.125rem] place-items-center rounded-xs border border-hairline bg-kagit font-mono text-micro text-ink-muted"
                  >
                    {process.code}
                  </span>

                  <Link
                    to="/uretim"
                    search={{ proses: process.code }}
                    title={`${reading} — üretim ekranını aç`}
                    className="truncate font-display text-data font-semibold tracking-tight text-ink after:absolute after:inset-0 after:content-[''] hover:text-kordovan"
                  >
                    {process.name}
                  </Link>

                  <span className="flex h-5 w-full min-w-8 items-stretch overflow-hidden rounded-xs bg-kagit">
                    <span className="bg-kordovan" style={{ width: `${inProgressPct}%` }} />
                    <span className="bg-kosele/22" style={{ width: `${waitingPct}%` }} />
                  </span>

                  <span className="text-right font-mono tnum text-[0.9375rem] font-medium text-ink">
                    {formatPairs(process.totalPairs)}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>

      <footer className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-hairline px-3.5 py-2 text-micro text-ink-muted">
        <span>
          Toplam{' '}
          <span className="font-mono tnum font-medium text-ink">
            {formatPairs(production.totalPairs)}
          </span>{' '}
          çift atölyede.
        </span>
        <span>
          Ayrıca{' '}
          <span className="font-mono tnum font-medium text-ink">
            {formatPairs(production.notStartedPairs)}
          </span>{' '}
          çift, üretime verilmemiş {formatPairs(production.notStartedEmirCount)} emirde bekliyor.
        </span>
      </footer>
    </Card>
  )
}

function Legend() {
  return (
    <div className="flex shrink-0 items-center gap-3 text-micro text-ink-muted">
      <span className="flex items-center gap-1.5">
        <span aria-hidden className="h-2 w-3 rounded-[1px] bg-kordovan" />
        Tezgâhta
      </span>
      <span className="flex items-center gap-1.5">
        <span aria-hidden className="h-2 w-3 rounded-[1px] bg-kosele/22" />
        Sırada
      </span>
    </div>
  )
}
